import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel } from '@material-ui/core';
import { isEmpty } from 'lodash/fp';
import { Warehouse } from '../../../../generated/graphql';
import { FadeTransition, TooltipedInfoIcon } from '../../../../ui-kit';

interface RemovalConfirmationDialogProps {
    open: boolean;
    handleClose(): void;
    warehouses: Pick<Warehouse, 'id' | 'name'>[];
    warehouseCardName: string;
    handleStockCardRemove(warehouseIds: string[]): Promise<void>;
}

const RemovalConfirmationDialog = ({
    open,
    handleClose,
    warehouses,
    handleStockCardRemove,
    warehouseCardName,
}: RemovalConfirmationDialogProps) => {
    const { t } = useTranslation();
    const hasMultipleWarehouses = warehouses.length > 1;
    const warehouseIds = warehouses.map(({ id }) => id);
    const [selectedWarehouseIds, setSelectedWarehouseIds] = useState(warehouseIds);
    const handleRemove = async () => {
        await handleStockCardRemove(selectedWarehouseIds);
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const warehouseId = e.target.name;
        const newIds = checked
            ? [...selectedWarehouseIds, warehouseId]
            : selectedWarehouseIds.filter(id => id !== warehouseId);

        setSelectedWarehouseIds(newIds);
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={FadeTransition}
            keepMounted
            onClose={handleClose}
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogTitle id="alert-dialog-slide-title">
                {t('Remove {{name}}', { name: warehouseCardName })}
                <TooltipedInfoIcon
                    tooltip={t(`You can only delete a warehouse card if it is not used in a recipe`) as string}
                    tooltipFontSize='1rem'
                />
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-slide-description">
                    {hasMultipleWarehouses
                        ? (
                            <>
                                {t('Choose warehouses to remove {{name}} from', { name: warehouseCardName })}
                                <Box display='grid'>
                                    {warehouses.map(({ id: warehouseId, name }) => (
                                        <FormControlLabel
                                            key={warehouseId}
                                            control={(
                                                <Checkbox
                                                    color='primary'
                                                    name={warehouseId}
                                                    defaultChecked={!!selectedWarehouseIds.find(id => warehouseId === id)}
                                                    checked={!!selectedWarehouseIds.find(id => warehouseId === id)}
                                                    onChange={handleCheckboxChange}
                                                />
                                        )}
                                            label={name}
                                        />
                                ))}
                                </Box>
                            </>
                        )
                        : t('Are you sure you want to remove {{name}}?', { name: warehouseCardName })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant='outlined' color="primary">
                    {t('Cancel')}
                </Button>
                <Button onClick={handleRemove} disabled={isEmpty(selectedWarehouseIds)} variant='outlined' color="secondary">
                    {t('Remove')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RemovalConfirmationDialog;